import React, { useMemo } from "react";
import { ckdEpi2021, mdrdIndian, urineACR, fib4, fib4Interp, eAG } from "./ClinicalCalculators";
import { Calculator, Plus, TrendingUp } from "lucide-react";

function num(v) { const n = parseFloat(v); return Number.isFinite(n) ? n : null; }
function findInv(list, name) {
  const needle = String(name).toLowerCase();
  const row = (list || []).find((x) => String(x.test_name || "").toLowerCase() === needle);
  return row?.result || "";
}

// Colour a chip by threshold hint. Green safe, amber caution, red danger.
function chipTone(kind, v) {
  if (v == null || v === "") return "bg-slate-100 text-slate-600 border-slate-200";
  const n = Number(v);
  if (kind === "egfr") {
    if (n >= 90) return "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (n >= 60) return "bg-lime-50 text-lime-800 border-lime-200";
    if (n >= 45) return "bg-amber-50 text-amber-800 border-amber-200";
    if (n >= 30) return "bg-orange-50 text-orange-800 border-orange-200";
    return "bg-red-50 text-red-800 border-red-200";
  }
  if (kind === "acr") {
    if (n < 30) return "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (n < 300) return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-red-50 text-red-800 border-red-200";
  }
  if (kind === "fib4") {
    if (n < 1.3) return "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (n < 2.67) return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-red-50 text-red-800 border-red-200";
  }
  if (kind === "eag") {
    if (n < 154) return "bg-emerald-50 text-emerald-800 border-emerald-200";
    if (n < 183) return "bg-amber-50 text-amber-800 border-amber-200";
    return "bg-red-50 text-red-800 border-red-200";
  }
  return "bg-slate-100 text-slate-700 border-slate-200";
}

function ckdStage(egfr) {
  if (egfr == null) return "";
  const n = Number(egfr);
  if (n >= 90) return "G1";
  if (n >= 60) return "G2";
  if (n >= 45) return "G3a";
  if (n >= 30) return "G3b";
  if (n >= 15) return "G4";
  return "G5";
}

// KDIGO 2024 heat-map risk category from combined eGFR (G) + ACR (A) stage.
// Returns { label, tone } — tone follows the standard green/yellow/orange/red
// four-tier risk grid.
function dkdRisk(egfr, acr) {
  if (egfr == null || acr == null) return null;
  const g = ckdStage(egfr);
  const a = acr < 30 ? "A1" : acr < 300 ? "A2" : "A3";
  // Green = low, Yellow = moderately increased, Orange = high, Red = very high
  const grid = {
    G1: { A1: "green", A2: "yellow", A3: "orange" },
    G2: { A1: "green", A2: "yellow", A3: "orange" },
    G3a: { A1: "yellow", A2: "orange", A3: "red" },
    G3b: { A1: "orange", A2: "red", A3: "red" },
    G4: { A1: "red", A2: "red", A3: "red" },
    G5: { A1: "red", A2: "red", A3: "red" },
  };
  const risk = grid[g]?.[a] || "green";
  const labelMap = { green: "Low risk", yellow: "Moderate", orange: "High risk", red: "Very high risk" };
  const toneMap = {
    green: "bg-emerald-50 text-emerald-800 border-emerald-200",
    yellow: "bg-amber-50 text-amber-800 border-amber-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    red: "bg-red-50 text-red-800 border-red-200",
  };
  return { g, a, label: `DKD ${g}/${a} — ${labelMap[risk]}`, tone: toneMap[risk] };
}

// Live compute strip: the moment enough lab values are typed the badge
// materialises with a colour-coded interpretation and a one-tap Push button
// that saves the derived value as its own investigation row (so it prints).
export default function LiveCalcStrip({ investigations, age, sex, onPushInvestigation }) {
  const derived = useMemo(() => {
    const scr = num(findInv(investigations, "Creatinine"));
    const hba1c = num(findInv(investigations, "HbA1c"));
    const ualb = num(findInv(investigations, "Urine Albumin"));
    const ucr = num(findInv(investigations, "Urine Creatinine"));
    const ast = num(findInv(investigations, "AST"));
    const alt = num(findInv(investigations, "ALT"));
    const plt = num(findInv(investigations, "Platelets"));
    const egfrCkd = scr && age ? ckdEpi2021(scr, age, sex) : null;
    const egfrMdr = scr && age ? mdrdIndian(scr, age, sex) : null;
    const acrRaw = findInv(investigations, "UACR (Urine ACR)");
    const acr = num(acrRaw) ?? (ualb && ucr ? urineACR(ualb, ucr) : null);
    const fib = age && ast && plt && alt ? fib4(age, ast, plt, alt) : null;
    const eag = hba1c ? eAG(hba1c) : null;
    return { egfrCkd, egfrMdr, acr, fib, eag, scr, hba1c, ualb, ucr, ast, alt, plt };
  }, [investigations, age, sex]);

  const chips = [];
  if (derived.egfrCkd !== null && derived.egfrCkd !== undefined) {
    chips.push({
      key: "egfr",
      title: "eGFR (CKD-EPI 2021)",
      value: derived.egfrCkd,
      unit: "mL/min/1.73m²",
      subtitle: `CKD ${ckdStage(derived.egfrCkd)}`,
      tone: chipTone("egfr", derived.egfrCkd),
      push: () => onPushInvestigation({ test_name: "eGFR (CKD-EPI 2021)", result: String(derived.egfrCkd), unit: "mL/min/1.73m²", reference_range: "> 90 normal", test_date: new Date().toISOString().slice(0, 10) }),
    });
  }
  if (derived.acr !== null && derived.acr !== undefined) {
    const stage = derived.acr < 30 ? "A1 (normal)" : derived.acr < 300 ? "A2 (micro)" : "A3 (macro)";
    chips.push({
      key: "acr",
      title: "Urine ACR",
      value: derived.acr,
      unit: "mg/g",
      subtitle: stage,
      tone: chipTone("acr", derived.acr),
      push: () => onPushInvestigation({ test_name: "UACR (Urine ACR)", result: String(derived.acr), unit: "mg/g", reference_range: "< 30", test_date: new Date().toISOString().slice(0, 10) }),
    });
  }
  // Combined DKD stage (KDIGO 2024 heat-map) — only when BOTH eGFR & ACR present.
  const dkd = dkdRisk(derived.egfrCkd, derived.acr);
  if (dkd) {
    chips.push({
      key: "dkd",
      title: "DKD stage (KDIGO 2024)",
      value: `${dkd.g}/${dkd.a}`,
      unit: "",
      subtitle: dkd.label.replace(`${dkd.g}/${dkd.a} — `, ""),
      tone: dkd.tone,
    });
  }
  if (derived.fib !== null && derived.fib !== undefined) {
    chips.push({
      key: "fib4",
      title: "FIB-4",
      value: derived.fib,
      unit: "",
      subtitle: fib4Interp(derived.fib),
      tone: chipTone("fib4", derived.fib),
      push: () => onPushInvestigation({ test_name: "FIB-4", result: String(derived.fib), unit: "", reference_range: "< 1.3 low / > 2.67 high", test_date: new Date().toISOString().slice(0, 10) }),
    });
  }
  if (derived.eag !== null && derived.eag !== undefined) {
    chips.push({
      key: "eag",
      title: "eAG (from HbA1c)",
      value: derived.eag,
      unit: "mg/dL",
      subtitle: `HbA1c ${derived.hba1c}%`,
      tone: chipTone("eag", derived.eag),
      push: () => onPushInvestigation({ test_name: "eAG", result: String(derived.eag), unit: "mg/dL", reference_range: "< 154 (~7% A1c)", test_date: new Date().toISOString().slice(0, 10) }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="mt-3 border border-pink-200 bg-gradient-to-br from-pink-50/50 to-white rounded-md p-3" data-testid="live-calc-strip">
      <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider text-pink-700 font-semibold">
        <Calculator size={11} /> Live calculations
        <span className="ml-auto text-[9px] text-slate-500 font-normal normal-case tracking-normal flex items-center gap-1">
          <TrendingUp size={9} /> auto-updates as you type lab values
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <div
            key={c.key}
            className={`border rounded-md px-2.5 py-1.5 flex items-center gap-2 ${c.tone}`}
            data-testid={`live-calc-${c.key}`}
          >
            <div className="flex flex-col min-w-0">
              <span className="text-[9px] uppercase tracking-wider font-semibold opacity-80">{c.title}</span>
              <span className="flex items-baseline gap-1">
                <span className="text-sm font-semibold tabular">{c.value}</span>
                {c.unit && <span className="text-[10px] opacity-70">{c.unit}</span>}
              </span>
              <span className="text-[10px] opacity-80">{c.subtitle}</span>
            </div>
            <button
              onClick={c.push}
              disabled={!c.push}
              className={`ml-1 p-1 rounded ${c.push ? "hover:bg-white/60" : "opacity-30 cursor-not-allowed"}`}
              title={c.push ? "Add this derived value as its own investigation row (prints on Rx)" : "Derived label — no separate row to push"}
              data-testid={`live-calc-push-${c.key}`}
            >
              <Plus size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
